import * as Adaptive from "adaptivecards";
import { TypedCardElementPeer, PropertySheet, PropertySheetCategory, StringPropertyEditor } from "adaptivecards-designer";
import { removeOtherPropertyConfigurations } from "../Utils/CustomElementUtils";
import { getPropertyEditorInstance, PropertyKeys } from "../Pickers/PickerBase";
import { ICustomProperty } from "../../../shared/interfaces/customproperties.interface";

const displayLabelProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayLabel", false);
const displayTitleProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "displayTitle", false);
const requireFieldProperty = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "requireField");
const isCustomPropertyProp = new Adaptive.BoolProperty(Adaptive.Versions.v1_0, "isCustomProperty", false);
const dataTypeProperty = new Adaptive.StringProperty(Adaptive.Versions.v1_0, "dataType", true);

export class BoolProperty extends Adaptive.TextInput {
    static readonly displayLabelProperty = displayLabelProperty;
    static readonly displayTitleProperty = displayTitleProperty;
    static readonly isCustomPropertyProp = isCustomPropertyProp;
    static readonly requireFieldProperty = requireFieldProperty;
    static readonly dataTypeProperty = dataTypeProperty;

    static JsonTypeName;
    static ElementId;
    static defaultLabel;
    static customPropertyData: ICustomProperty;

    constructor(paramType, paramCustomPropertyData: ICustomProperty) {
        
        BoolProperty.JsonTypeName = paramType ?? BoolProperty.JsonTypeName;
        BoolProperty.ElementId = paramCustomPropertyData?.PropertyId ?? BoolProperty.ElementId;
        BoolProperty.defaultLabel = paramCustomPropertyData?.Name ?? BoolProperty.defaultLabel;
        BoolProperty.customPropertyData = paramCustomPropertyData ?? BoolProperty.customPropertyData;
        
        super();
    }

    //#region Schema

    @Adaptive.property(displayLabelProperty)
    get displayLabel(): string | undefined {
        return this.getValue(BoolProperty.displayLabelProperty);
    }

    set displayLabel(value: string | undefined) {
        this.setValue(BoolProperty.displayLabelProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(displayTitleProperty)
    get displayTitle(): string | undefined {
        return this.getValue(BoolProperty.displayTitleProperty);
    }

    set displayTitle(value: string | undefined) {
        this.setValue(BoolProperty.displayTitleProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(requireFieldProperty)
    get requireField(): boolean | undefined {
        return this.getValue(BoolProperty.requireFieldProperty);
    }

    set requireField(value: boolean | undefined) {
        this.setValue(BoolProperty.requireFieldProperty, value);
        this.updateLayout();
    }

    @Adaptive.property(isCustomPropertyProp)
    get isCustomProperty(): boolean | undefined {
        return this.getValue(BoolProperty.isCustomPropertyProp);
    }

    set isCustomProperty(value: boolean | undefined) {
        this.setValue(BoolProperty.isCustomPropertyProp, value);
        this.updateLayout();
    }

    @Adaptive.property(dataTypeProperty)
    get dataType(): string | undefined {
        return this.getValue(BoolProperty.dataTypeProperty);
    }

    set dataType(value: string | undefined) {
        this.setValue(BoolProperty.dataTypeProperty, value);
        this.updateLayout();
    }

    internalRender(): HTMLElement | undefined {
        if((this?.id ?? "").length == 0)
            this.id = BoolProperty.ElementId;

        if(!(this?.isCustomProperty ?? false))
            this.isCustomProperty = true;

        this.displayTitle ??= BoolProperty.defaultLabel;

        if((this?.dataType ?? "").length == 0)
            this.dataType = BoolProperty.customPropertyData?.DataType ?? "";

        if(this.requireField == undefined){
            this.requireField = BoolProperty.customPropertyData?.IsRequired ?? false;
            if(this.requireField && this.displayLabel == undefined)
                this.displayLabel = BoolProperty.defaultLabel;
        }

        this.errorMessage ??= BoolProperty.customPropertyData?.ErrorMessage ?? "";

        let cardElement = new Adaptive.ToggleInput();
        cardElement.setParent(this);
        cardElement["label"] = this.displayLabel;
        cardElement["title"] = this.displayTitle;
        cardElement["isRequired"] = this.requireField;
        cardElement["errorMessage"] = this.errorMessage;

        if(this.defaultValue)
            cardElement["defaultValue"] = this.defaultValue;
        const renderedElement =  cardElement.render();
        this.inputControlContainerElement.style.display = "block";

        this.inputControlContainerElement.classList?.add("tikit-customProperty");
        return renderedElement;
    }

    getJsonTypeName(): string {
        return BoolProperty.JsonTypeName;
    }
}

export class BoolPropertyPeer extends TypedCardElementPeer<BoolProperty> {
    static readonly displayLabelProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayLabel", "Label", false);
    static readonly displayTitleProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "displayTitle", "Title", false);
    static requireFieldProperty = getPropertyEditorInstance(BoolProperty.ElementId, PropertyKeys.requireField);
    static readonly requiredMessageProperty = new StringPropertyEditor(Adaptive.Versions.v1_0, "errorMessage", "Error Message", false);

    populatePropertySheet(propertySheet: PropertySheet, defaultCategory: string = PropertySheetCategory.DefaultCategory) {
        super.populatePropertySheet(propertySheet, defaultCategory);
        removeOtherPropertyConfigurations(propertySheet);

        propertySheet.add(
            defaultCategory,
            BoolPropertyPeer.idProperty,
            BoolPropertyPeer.displayLabelProperty,
            BoolPropertyPeer.displayTitleProperty
        );

        propertySheet.add(
            "Validation",            
            BoolPropertyPeer.requireFieldProperty,
            BoolPropertyPeer.requiredMessageProperty
        );
    }
}